import { combineLatest, from, merge, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, mergeMap, retry, switchMap, tap } from 'rxjs/operators';

import { userInfo$, authorizedFetch, readJson } from './http.js';
import { showError } from './ui.js';
import { appendConversation } from './conversations.js';

let userName$ = userInfo$.pipe(
    map(userInfo => (userInfo.oauth.given_name || '').trim()),
    distinctUntilChanged(),
    map(name => {
        let len = name.length;
        if (len === 0) {
            return '';
        }
        if (name[len-1] === 's') {
            return `${name}' `;
        } else {
            return `${name}'s `;
        }
    }),
    tap(name => document.getElementById('user-given-name').textContent = name),
);


let timeGroups$ = of(undefined).pipe(
    map(() => {
        let now = new Date();
        return [
            {
                title: 'Today',
                limit: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
            },
            {
                title: 'Yesterday',
                limit: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1),
            },
            {
                title: 'Current Week',
                limit: new Date(now.getFullYear(), now.getMonth(), now.getDate() - (now.getDay() - 1) % 7),
            },
            {
                title: 'Last Week',
                limit: new Date(now.getFullYear(), now.getMonth(), now.getDate() - (now.getDay() - 1) % 7 - 7),
            },
            {
                title: 'Current Month',
                limit: new Date(now.getFullYear(), now.getMonth(), 1),
            },
            {
                title: 'Last Month',
                limit: new Date(now.getFullYear(), now.getMonth() - 1, 1),
            },
            {
                title: 'Ancient',
                limit: null,
            },
        ];
    }),
);

let history$ = of(undefined).pipe(
    switchMap(() => from(authorizedFetch('/api/conversation-history'))),
    readJson(),
    retry({
        count: 3,
        delay: 1000 + 500*Math.random(),
    }),
);

let groupedHistory$ = combineLatest([
    history$,
    timeGroups$,
]).pipe(
    map(([history, timeGroups]) => {
        let historyGroups = [];
        for (let conv of history) {
            let created = new Date(conv.created);
            let group = null;
            for (let tg of timeGroups) {
                if (!tg.limit || created >= tg.limit) {
                    group = tg;
                    break;
                }
            }
            let hg = null;
            if (historyGroups.length === 0 || historyGroups[historyGroups.length - 1].timeGroup !== group) {
                hg = {
                    timeGroup: group,
                    conversations: [],
                };
                historyGroups.push(hg);
            } else {
                hg = historyGroups[historyGroups.length - 1];
            }
            hg.conversations.push(conv);
        }
        return historyGroups;
    }),
    tap(historyGroups => {
        let container = document.getElementById('conversation-groups');
        container.innerHTML = '';
        for (let group of historyGroups) {
            let groupLi = document.createElement('li');
            groupLi.classList.add('time-group');
            container.appendChild(groupLi);
            let groupHl = document.createElement('h2');
            groupHl.textContent = group.timeGroup.title;
            groupLi.appendChild(groupHl);
            let conversationsContainer = document.createElement('ol');
            conversationsContainer.classList.add('conversations');
            groupLi.appendChild(conversationsContainer);
            for (let conv of group.conversations) {
                let li = document.createElement('li');
                li.classList.add('conversation');
                li.setAttribute('data-id', conv.id);
                let actionsEl = document.createElement('div');
                li.appendChild(actionsEl);
                let undoArchiveEl = document.createElement('button');
                undoArchiveEl.classList.add('undo-archive-action');
                undoArchiveEl.textContent = '⮏';
                undoArchiveEl.type = 'button';
                undoArchiveEl.title = 'undo archive';
                undoArchiveEl.addEventListener('click', handleUndoArchiveConversation, false);
                actionsEl.appendChild(undoArchiveEl);
                let archiveEl = document.createElement('button');
                archiveEl.classList.add('archive-action');
                archiveEl.textContent = '🗑';
                archiveEl.type = 'button';
                archiveEl.title = 'archive';
                archiveEl.addEventListener('click', handleArchiveConversation, false);
                actionsEl.appendChild(archiveEl);
                let convLink = document.createElement('a');
                convLink.href = `/#conv/${conv.id}`;
                convLink.textContent = conv.title || conv.id;
                li.appendChild(convLink);
                conversationsContainer.appendChild(li);
            }
        }
    }),
);

let archiveConversationTrigger$ = new Subject();

function handleUndoArchiveConversation(event) {
    let convEl = getConversationElement(event.target);
    let convId = convEl.getAttribute('data-id');
    archiveConversationTrigger$.next([convId, false]);
    convEl.classList.remove('archived');
}

function handleArchiveConversation(event) {
    let convEl = getConversationElement(event.target);
    let convId = convEl.getAttribute('data-id');
    archiveConversationTrigger$.next([convId, true]);
    convEl.classList.add('archived');
}

function getConversationElement(element) {
    while (element) {
        if (element.classList.contains('conversation')) {
            return element;
        }
        element = element.parentNode;
    }
    return null;
}

let archiveConversations$ = archiveConversationTrigger$.pipe(
    mergeMap(([convId, archived]) => appendConversation(convId, {
        archived,
    }), 3),
);

merge(
    userName$,
    groupedHistory$,
    archiveConversations$,
).pipe().subscribe({
    error: err => showError(err),
});
